<template>
  <div class="container-fluid">
    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Visión general de compras</h1>
          <p class="view-header__subtitle">Resumen de compras y pagos por método.</p>
        </div>
      </div>
    </div>

    <!-- STATS -->
    <div class="row">
      <div class="col-12 col-md-3">
        <div class="stats-card">
          <div class="stats-card__value">{{ formatoDinamicoMoneda(purchases.total) }}</div>
          <div class="stats-card__label"><i class="bi bi-receipt-cutoff"></i> Total en compras</div>
        </div>
      </div>
      <div class="col-12 col-md-3">
        <div class="stats-card">
          <div class="stats-card__value">{{ formatoDinamicoMoneda(purchases.cash) }}</div>
          <div class="stats-card__label"><i class="bi bi-cash-coin"></i> Efectivo</div>
        </div>
      </div>
      <div class="col-12 col-md-3">
        <div class="stats-card">
          <div class="stats-card__value">{{ formatoDinamicoMoneda(purchases.cc) }}</div>
          <div class="stats-card__label"><i class="bi bi-journal-text"></i> Cuenta corriente</div>
        </div>
      </div>
      <div class="col-12 col-md-3">
        <div class="stats-card">
          <div class="stats-card__value">{{ formatoDinamicoMoneda(purchases.transfers || 0) }}</div>
          <div class="stats-card__label"><i class="bi bi-arrow-left-right"></i> Transferencias</div>
        </div>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <q-select v-model="invoiceType" :options="invoiceTypeOptions" />
          </div>
          <div class="filters-panel__field filters-panel__field--inline">
            <label class="control-label">Fecha:</label>
            <Daterange :opendir="'left'" :filtered="true" @datepicked="dateCreated"/>
          </div>
          <div class="filters-panel__actions">
            <button type="button" class="filters-panel__btn" @click="exportCSV">
              <i class="bi bi-filetype-csv"></i>
              Exportar CSV
            </button>
            <button type="button" class="filters-panel__btn" @click="exportPDF">
              <i class="bi bi-filetype-pdf"></i>
              Exportar PDF
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- TABLA -->
    <div class="data-table">
      <vdtnet-table
        ref="table"
        :fields="fields"
        :opts="opts"
        :selectCheckbox="0"
        container-class-name="table-responsive"
        class-name="table mb-0"
      />
    </div>
  </div>
</template>

<script>
import VdtnetTable from '@/components/global/VdtnetTable.vue';
import formatDateMixin from "../../mixins/formatDateMixin";
import formatMoneyMixin from "../../mixins/formatMoneyMixin";
import Daterange from "../global/Daterange-component.vue";
import QSelect from '@/components/shared/QSelect.vue';

export default {
  components: {
    VdtnetTable,
    Daterange,
    QSelect,
  },
  mixins: [formatMoneyMixin, formatDateMixin],
  data: function () {
    return {
      invoiceType: "",
      startDate: null,
      endDate: null,
      purchases: {},
      opts: {
        language: {
          url: '//cdn.datatables.net/plug-ins/1.13.4/i18n/es-ES.json',
        },
        ajax: {
          url: process.env.VUE_APP_BASE_URL + "/api/reports/purchases",
          type: "GET",
          data: (d) => {
            d.invoiceType = this.invoiceType;
            d.daterange = this.daterange;
            return d;
          },
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        },
        processing: true,
        pageLength: 15,
        searching: true,
        searchDelay: 400,
        destroy: true,
        order: [[0, 'desc']],
        ordering: true,
        lengthChange: true,
        autoWidth: false,
      },
      fields: {
        created_at: {
          label: "Fecha",
          searchable: true,
          render: (data) => this.formatoFechaHora(data),
        },
        type: { label: "Tipo" },
        number: { label: "Número" },
        provider_name: {
          label: "Proveedor",
          render: (data, type) =>
            type === 'display' ? `<span class="data-table__primary">${data || ''}</span>` : data,
        },
        total: {
          label: "Monto",
          align: "right",
          searchable: true,
          render: this.formatoDinamicoMoneda,
        },
      },
    };
  },
  computed: {
    invoiceTypeOptions() {
      return [
        { value: '', label: 'Todos los tipos' },
        { value: 'FC', label: 'FC' },
        { value: 'NC', label: 'NC' },
        { value: 'ND', label: 'ND' },
      ];
    },
    daterange() {
      if (this.startDate === null || this.endDate === null) return null;
      const startDate = this.formatoFecha2Daterange(this.startDate);
      const endDate = this.formatoFecha2Daterange(this.endDate);
      return `${startDate} - ${endDate}`;
    },
    filters() {
      return {
        invoiceType: this.invoiceType,
        daterange: this.daterange,
      };
    },
  },
  watch: {
    filters: {
      handler() {
        this.reload();
      },
      deep: true,
    },
  },
  mounted() {
    this.setDefaultDateRange();
    this.getTotals();
  },
  methods: {
    dateCreated(date) {
      this.startDate = date.startDate;
      this.endDate = date.endDate;
    },
    reload() {
      this.getTotals();
      this.refreshTable();
    },
    refreshTable() {
      if (this.$refs.table !== undefined) this.$refs.table.reload();
    },
    getTotals() {
      this.$http.get("/api/reports/statsByPurchases", { params: this.filters })
        .then((response) => {
          this.purchases = response.data.data;
        })
        .catch((error) => {
          console.error('getTotals:', error);
        });
    },
    exportCSV() {
      this.$http.post("/api/reports/CSVpurchaseReport", this.filters);
    },
    exportPDF() {
      this.$http.post("/api/reports/PDFpurchaseReport", this.filters);
    },
    setDefaultDateRange() {
      const today = new Date();
      const startDate = new Date();
      startDate.setDate(today.getDate() - 30);
      this.startDate = this.formatDate(startDate);
      this.endDate = this.formatDate(today);
    },
    formatDate(date) {
      const year = date.getFullYear();
      const month = ('0' + (date.getMonth() + 1)).slice(-2);
      const day = ('0' + date.getDate()).slice(-2);
      return `${day}/${month}/${year}`;
    },
  },
};
</script>

<style scoped>
.filters-panel__field--inline {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filters-panel__field--inline .control-label {
  margin: 0;
  white-space: nowrap;
  font-size: 13px;
  color: var(--text);
}

.filters-panel__field--inline > .form-group {
  flex: 1 1 auto;
  margin-bottom: 0;
}
</style>
